import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { organizerNotificationPreferencesApi, organizerUpdateNotificationPreferencesApi } from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { handleError } from "../../helpers/handleError";
import { OrganizerHeaderContext } from "../Layout";
import "../dashboard.css";
import "../components/ZocoToggle.css";
import "./NotificationPreferences.css";

// Mismo orden que el menú del portal. `clave: null` = aviso obligatorio: se muestra activo, bloqueado
// y nunca se manda al back (no es una preferencia, es un deber legal).
const SECCIONES = [
  {
    id: "eventos",
    titulo: "Tickets",
    filas: [
      {
        clave: null,
        etiqueta: "Solicitud de arrepentimiento",
        descripcion: "Cuando un comprador pide arrepentirse de una compra.",
        nota: "Siempre te avisamos: tiene un plazo legal.",
      },
      {
        clave: "eventos.sales_milestone",
        etiqueta: "Hitos de venta",
        descripcion: "Cuando un evento llega al 50%, 80% y 100% vendido.",
      },
      {
        clave: "eventos.event_reminder",
        etiqueta: "Recordatorio de evento",
        descripcion: "Un aviso antes de que empiece cada evento.",
      },
      {
        clave: "eventos.event_summary",
        etiqueta: "Resumen del evento",
        descripcion: "Un resumen de ventas y asistencia cuando el evento termina.",
      },
    ],
  },
  {
    id: "mesas",
    titulo: "Reservas de mesas",
    filas: [
      {
        clave: "mesas.reservation_confirmed",
        etiqueta: "Reserva confirmada",
        descripcion: "Cuando un cliente confirma una reserva.",
      },
      {
        clave: "mesas.reservation_cancelled",
        etiqueta: "Reserva cancelada",
        descripcion: "Cuando un cliente cancela una reserva.",
        nota: "Si hay que devolver una seña, te avisamos igual.",
      },
    ],
  },
  {
    id: "canchas",
    titulo: "Canchas",
    filas: [
      {
        clave: "canchas.reservation_confirmed",
        etiqueta: "Reserva confirmada",
        descripcion: "Cuando un cliente confirma una reserva.",
      },
      {
        clave: "canchas.reservation_cancelled",
        etiqueta: "Reserva cancelada",
        descripcion: "Cuando un cliente cancela una reserva.",
        nota: "Si hay que devolver una seña, te avisamos igual.",
      },
      {
        clave: "canchas.birthday_requested",
        etiqueta: "Solicitudes de cumpleaños",
        descripcion: "Cuando un cliente pide un cumpleaños y tenés que responderle.",
      },
    ],
  },
];

const NotificationPreferences = () => {
  const { setPageHeader } = useContext(OrganizerHeaderContext);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [desactivadas, setDesactivadas] = useState([]);
  const [guardando, setGuardando] = useState([]);
  // Lista vigente para armar el PUT aunque dos switches se toquen seguidos.
  const actual = useRef([]);

  useEffect(() => {
    setPageHeader({
      title: "Notificaciones",
      subtitle: "Elegí qué avisos te llegan a la campana del portal de ZOCO.",
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const aplicar = (lista) => {
    actual.current = lista;
    setDesactivadas(lista);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await organizerNotificationPreferencesApi();
      const lista = res?.data?.info?.desactivadas;
      aplicar(Array.isArray(lista) ? lista : []);
    } catch (error) {
      setLoadError(true);
      handleError(error, "No se pudieron cargar tus preferencias de notificaciones.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const alternar = async (clave) => {
    if (guardando.includes(clave)) return;
    const estabaApagada = actual.current.includes(clave);
    const siguiente = estabaApagada ? actual.current.filter((k) => k !== clave) : [...actual.current, clave];
    aplicar(siguiente);
    setGuardando((prev) => [...prev, clave]);
    try {
      await organizerUpdateNotificationPreferencesApi(siguiente);
    } catch (error) {
      // Se revierte solo esta clave: otra que se guardó mientras tanto no se pisa.
      aplicar(estabaApagada ? [...actual.current, clave] : actual.current.filter((k) => k !== clave));
      handleError(error, "No se pudo guardar el cambio. Intentá de nuevo.");
    } finally {
      setGuardando((prev) => prev.filter((k) => k !== clave));
    }
  };

  if (loading) return <SpinLoading />;

  if (loadError) {
    return (
      <div className="organizer-page organizer-notif-prefs">
        <section className="organizer-section">
          <div className="organizer-form-card" role="alert">
            <p className="organizer-notif-prefs__error">
              No pudimos cargar tus preferencias. Tus avisos siguen llegando como siempre.
            </p>
            <div>
              <button type="button" className="btn btn-dark" onClick={cargar}>
                Reintentar
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="organizer-page organizer-notif-prefs">
      <section className="organizer-section">
        <div className="organizer-notif-prefs__groups">
          {SECCIONES.map((seccion) => (
            <section key={seccion.id} className="organizer-form-card" aria-labelledby={`notif-${seccion.id}`}>
              <h3 id={`notif-${seccion.id}`}>{seccion.titulo}</h3>
              <ul className="organizer-notif-prefs__list">
                {seccion.filas.map((fila) => {
                  const obligatoria = fila.clave === null;
                  const activa = obligatoria || !desactivadas.includes(fila.clave);
                  const descId = `notif-desc-${seccion.id}-${fila.etiqueta}`.replace(/s+/g, "-");
                  return (
                    <li key={fila.etiqueta} className="organizer-notif-prefs__row">
                      <div className="organizer-notif-prefs__text">
                        <span className="organizer-notif-prefs__label">{fila.etiqueta}</span>
                        <span id={descId} className="organizer-notif-prefs__desc">
                          {fila.descripcion}
                        </span>
                        {fila.nota && <span className="organizer-notif-prefs__note">{fila.nota}</span>}
                      </div>
                      <button
                        type="button"
                        className={"zoco-toggle" + (activa ? " is-on" : "")}
                        role="switch"
                        aria-checked={activa}
                        aria-label={fila.etiqueta}
                        aria-describedby={descId}
                        disabled={obligatoria || guardando.includes(fila.clave)}
                        onClick={() => alternar(fila.clave)}
                      >
                        <span className="knob" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
};

export default NotificationPreferences;
